import {
  Box,
  BoxProps,
  Flex,
  Text,
  FlexProps,
  Heading,
  HStack,
  Button,
  IconProps,
  ComponentWithAs,
  Skeleton,
  SpinnerProps,
  ChakraComponent,
} from '@chakra-ui/react'
import React, { FC, useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { noop } from '@chakra-ui/utils'
import { useLocalization } from '@rw1/localization'
import { BodyBackgroundNuance, useColor } from '../../theme'
import { useImageBackground } from '../../hooks/useImageBackground'
import { InfoOutline } from '../../icons'
import { AndroidTooltip } from '../AndroidTooltip'
import { AnimatePresenceWrapper } from './AnimatePresenceWrapper'

export const TILE_BORDER_RADIUS: FlexProps['borderRadius'] = '2xl'
export const TILE_HEIGHT: FlexProps['height'] = '13rem'
const IMAGE_HEIGHTL: FlexProps['height'] = '9.7rem'

export const TILE_PADDING: FlexProps['padding'] = 4
export const DEFAULT_TILE_WIDTH: FlexProps['width'] = '19rem'

export const DEFAULT_TILE_SPACING = 8

const AnimatedStack = motion(HStack)

interface ITileProps extends BoxProps {
  children?: React.ReactNode
  handleClick?: () => void
  handleAction: () => void
  id?: string
  image: string
  title: string
  caption?: string
  showAction?: boolean
  actionDisabled?: boolean
  actionCaption?: string
  actionText: string
  actionIcon:
  | ChakraComponent<ComponentWithAs<"svg", IconProps>>
  | ComponentWithAs<"svg", IconProps>
  | ComponentWithAs<"svg", SpinnerProps>
  | FC<IconProps>
  currentIcon?:
  | ChakraComponent<ComponentWithAs<"svg", IconProps>>
  | ComponentWithAs<"svg", IconProps>
  | ComponentWithAs<"svg", SpinnerProps>
  | FC<IconProps>
  currentIconColor?: string
  isAndroid?: boolean
  isInstalled?: boolean
  showBubbleText?: boolean
  bubbleText?: string
}

const Tile: FC<ITileProps> = ({
  actionCaption,
  actionDisabled = false,
  actionIcon,
  actionText,
  bubbleText,
  caption,
  handleAction,
  handleClick = noop,
  id,
  image,
  showAction = true,
  title,
  currentIcon,
  currentIconColor,
  isAndroid = false,
  isInstalled = true,
  showBubbleText,
  ...boxProps
}) => {
  const { localize } = useLocalization()
  const captionColor = useColor({ nuance: 3, palette: 'text' })
  const titleColor = useColor({ nuance: 6, palette: 'text' })
  const whiteColor = useColor({ nuance: 'white', palette: 'colors' })
  const backgroundColor = useColor({
    palette: 'body',
    nuance: BodyBackgroundNuance.Clean
  })

  const runningTextRef = useRef<HTMLDivElement>(null)
  const iconWrapperRef = useRef<HTMLDivElement>(null)
  const [tooltipMaxWidth, setTooltipMaxWidth] = useState('180px')

  useEffect(() => {
    function updateTooltipWidth() {
      if (runningTextRef.current && iconWrapperRef.current) {
        const textRect = runningTextRef.current.getBoundingClientRect()
        const iconRect = iconWrapperRef.current.getBoundingClientRect()
        const distance = iconRect.right - textRect.left
        setTooltipMaxWidth(`${distance}px`)
      }
    }

    updateTooltipWidth()
    window.addEventListener('resize', updateTooltipWidth)

    return () => {
      window.removeEventListener('resize', updateTooltipWidth)
    }
  }, [])

  const IconComponent = actionIcon
  const StateIcon = currentIcon ?? Box
  const stateIconColor = currentIconColor ?? ''

  const [isHovered, setIsHovered] = useState(false)
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const bgImageStyle = useImageBackground(image)

  return (
    <Box
      className="tileContainer"
      cursor="pointer"
      data-testid="tile-container"
      onClick={handleClick}
      onMouseEnter={() => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current)
          // eslint-disable-next-line functional/immutable-data
          hoverTimeoutRef.current = null
        }
        setIsHovered(true)
      }}
      onMouseLeave={() => {
        // eslint-disable-next-line functional/immutable-data
        hoverTimeoutRef.current = setTimeout(() => {
          setIsHovered(false)
          // eslint-disable-next-line functional/immutable-data
          hoverTimeoutRef.current = null
        }, 200)
      }}
      width="full"
      {...boxProps}
    >
      <Flex
        backgroundColor={backgroundColor}
        borderRadius={TILE_BORDER_RADIUS}
        boxShadow="md"
        color="white"
        filter="drop-shadow(0px 0px 5px rgba(0, 0, 0, 0.1))"
        flexDir="column"
        height={TILE_HEIGHT}
        overflow="hidden"
        position="relative"
        width="full"
      >
        {showBubbleText && (
          <Box
            alignItems="center"
            backgroundColor={backgroundColor}
            border="1px solid black"
            borderRadius={TILE_BORDER_RADIUS}
            boxShadow="md"
            left="5px"
            maxWidth="50%"
            padding="5px"
            position="fixed"
            top="5px"
            zIndex={100}
            {...boxProps}
          >
            <Text color="black" fontWeight={600} padding="5px">
              {bubbleText}
            </Text>
          </Box>
        )}

        <Flex
          alignItems="center"
          {...bgImageStyle}
          data-testid="tile-image"
          height={IMAGE_HEIGHTL}
          justifyContent="center"
          overflow="hidden"
          padding={TILE_PADDING}
          position="relative"
          width="full"
        />

        <Flex
          alignItems="flex-start"
          backgroundColor={whiteColor}
          bottom="0"
          flexDir="column"
          height="fit-content"
          padding={TILE_PADDING}
          paddingBottom={0}
          position="absolute"
          width="full"
        >
          <Box width="full">
            {caption && (
              <Text
                color={captionColor}
                data-testid="title-caption"
                fontSize="sm"
                fontWeight={700}
                noOfLines={1}
              >
                {caption}
              </Text>
            )}
            <HStack paddingBottom={TILE_PADDING}>
              <Heading
                color={titleColor}
                data-testid="tile-title"
                fontSize="xl"
                noOfLines={3}
                width="full"
                {...(id ? { id } : {})}
              >
                {title}
              </Heading>

              {isAndroid && !isInstalled ? (
                <div ref={iconWrapperRef}>
                  <AndroidTooltip
                    label={localize('contacts.experience_not_installed')}
                    maxWidth={tooltipMaxWidth}
                  >
                    <InfoOutline color={captionColor} />
                  </AndroidTooltip>
                </div>
              ) : (
                <StateIcon
                  boxSize={4}
                  color={stateIconColor}
                  height="22px"
                  marginLeft="auto"
                />
              )}
            </HStack>
          </Box>

          <AnimatePresenceWrapper>
            {isHovered && (
              <AnimatedStack
                animate={{
                  height: 48,
                  transition: { ease: 'easeOut' },
                }}
                data-testid="tile-hover-section"
                exit={{
                  height: 0,
                  transition: { ease: 'easeOut' },
                }}
                initial={{
                  height: 0,
                }}
                overflow="visible"
                width="100%"
              >
                <motion.div
                  animate={{
                    y: -16,
                    transition: { ease: 'easeOut' },
                  }}
                  exit={{
                    y: 48,
                    transition: { ease: 'easeOut' },
                  }}
                  initial={{
                    y: 48,
                    width: "100%"
                  }}
                >
                  {showAction ? (
                    <HStack width="full">
                      <Box
                        _hover={{
                          cursor: actionDisabled ? 'default' : 'pointer',
                        }}
                      >
                        <Button
                          _disabled={{
                            cursor: 'default',
                            opacity: 0.6,
                            pointerEvents: 'none',
                          }}
                          data-testid="tile-button"
                          flexShrink={0}
                          iconSpacing={2}
                          isDisabled={actionDisabled}
                          leftIcon={<IconComponent boxSize={4} />}
                          minWidth="6rem"
                          mt="4"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleAction()
                          }}
                          px={2}
                        >
                          {actionText}
                        </Button>
                      </Box>
                      <Text
                        color={captionColor}
                        fontSize="sm"
                        fontWeight={500}
                        mt="8px"
                        overflow="hidden"
                        paddingLeft={2}
                        textOverflow="ellipsis"
                        whiteSpace="pre"
                      >
                        {actionCaption}
                      </Text>
                    </HStack>
                  ) : (
                    <HStack>
                      <Skeleton height={8} width={32} />
                      <Skeleton height={4} width={24} />
                    </HStack>
                  )}
                </motion.div>
              </AnimatedStack>
            )}
          </AnimatePresenceWrapper>
        </Flex>
      </Flex>
    </Box>
  )
}

export { Tile }
